import React, { useState, useEffect, Suspense, lazy } from 'react'
import {
  Hammer,
  PlusCircle,
  ClipboardList,
  ShieldCheck,
  CheckCircle2,
  Home,
} from 'lucide-react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { ThemeProvider, ThemeSwitcher } from './context/ThemeContext'
import type {
  AiInquiryDraft,
  CompanyProfile,
  Inquiry,
  InquiryOffer,
  CompanyReview,
  CreditTransaction,
  TradeCategory,
  AppUser,
} from './types/poptavky'
import {
  auth,
  readCachedAllData,
  loadAllData,
  saveCompanyRecord,
  saveInquiryRecord,
  saveOfferRecord,
  saveReviewRecord,
  saveTransactionRecord,
  deleteInquiryRecord,
  saveUserRecord,
  ensureAuthSession,
  prewarmGoogleAuthPopup,
  loginWithGoogle,
  logoutAccount,
  syncAndLoadAppUser,
  generateAiInquiryTemplate,
} from './firebase'
import { LandingPage } from './components/LandingPage'
import { UserProfileMenu } from './components/UserProfileMenu'
import {
  parseUrlInquiryParams,
  updateDocumentSeo,
  registerBrowserAiAgentTools,
  type UrlInquiryPrefill,
} from './utils/seoAndAiAgent'
import { loadSavedInquiryDraft } from './utils/inquiryDraftStorage'

// Lazy-loaded pohledy (aby úvodní načtení stránky stahovalo minimum JS a dosáhlo maximálního PageSpeed skóre)
const NewInquiryWizard = lazy(() => import('./components/NewInquiryWizard'))
const ClientDashboard = lazy(() =>
  import('./components/ClientDashboard').then((m) => ({ default: m.ClientDashboard }))
)
const CompanyPortal = lazy(() =>
  import('./components/CompanyPortal').then((m) => ({ default: m.CompanyPortal }))
)
const AdminPortal = lazy(() =>
  import('./components/AdminPortal').then((m) => ({ default: m.AdminPortal }))
)
const CompanyReviewsModal = lazy(() =>
  import('./components/CompanyReviewsModal').then((m) => ({ default: m.CompanyReviewsModal }))
)
const ReviewFormView = lazy(() =>
  import('./components/ReviewFormView').then((m) => ({ default: m.ReviewFormView }))
)
const LegalPagesView = lazy(() =>
  import('./components/LegalPagesView').then((m) => ({ default: m.LegalPagesView }))
)

const PageLoadingSkeleton: React.FC = () => (
  <div className="w-full max-w-4xl mx-auto space-y-5 py-4 animate-fade-up" aria-busy="true">
    <div className="h-32 sm:h-40 rounded-3xl shimmer-box border border-slate-200 dark:border-slate-800" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="h-20 rounded-2xl shimmer-box border border-slate-200 dark:border-slate-800" />
      <div className="h-20 rounded-2xl shimmer-box border border-slate-200 dark:border-slate-800" />
      <div className="h-20 rounded-2xl shimmer-box border border-slate-200 dark:border-slate-800" />
    </div>
    <div className="h-72 rounded-3xl shimmer-box border border-slate-200 dark:border-slate-800" />
  </div>
)

type ActiveMainView =
  | 'landing'
  | 'new_inquiry'
  | 'client_dashboard'
  | 'company_portal'
  | 'admin_portal'
  | 'review_form'
  | 'privacy'
  | 'terms'

const GoogleIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98Z"
    />
  </svg>
)

export const AppContent: React.FC = () => {
  const [initialCache] = useState(() => readCachedAllData())
  const [activeView, setActiveView] = useState<ActiveMainView>('landing')
  const [selectedCategory, setSelectedCategory] = useState<TradeCategory | undefined>(undefined)
  const [selectedCategories, setSelectedCategories] = useState<TradeCategory[]>([])
  const [aiDraft, setAiDraft] = useState<AiInquiryDraft | null>(null)
  const [urlPrefill, setUrlPrefill] = useState<UrlInquiryPrefill | null>(null)
  const [currentUserUid, setCurrentUserUid] = useState<string>('local_user')
  const [googleUser, setGoogleUser] = useState<User | null>(null)
  const [currentAppUser, setCurrentAppUser] = useState<AppUser | null>(null)
  const [companies, setCompanies] = useState<CompanyProfile[]>(initialCache.companies)
  const [activeCompanyId, setActiveCompanyId] = useState<string>(
    initialCache.companies[0]?.id || 'comp_elektro_novak'
  )
  const [inquiries, setInquiries] = useState<Inquiry[]>(initialCache.inquiries)
  const [offers, setOffers] = useState<InquiryOffer[]>(initialCache.offers)
  const [reviews, setReviews] = useState<CompanyReview[]>(initialCache.reviews)
  const [reviewInquiryId, setReviewInquiryId] = useState<string | undefined>(undefined)
  const [inspectedCompany, setInspectedCompany] = useState<CompanyProfile | null>(null)
  const [transactions, setTransactions] = useState<CreditTransaction[]>(initialCache.transactions)
  const [users, setUsers] = useState<AppUser[]>(initialCache.users)
  const [companySubTab, setCompanySubTab] = useState<
    'feed' | 'my_offers' | 'credits' | 'profile' | 'register'
  >('feed')
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [hasSavedDraft, setHasSavedDraft] = useState<boolean>(() => Boolean(loadSavedInquiryDraft()))

  useEffect(() => {
    setHasSavedDraft(Boolean(loadSavedInquiryDraft()))
  }, [activeView])

  const showToast = (msg: string) => {
    setToastMessage(msg)
    setTimeout(() => {
      setToastMessage((prev) => (prev === msg ? null : prev))
    }, 4500)
  }

  // Zjištění, zda je aktuálně přihlášený uživatel skutečný Admin v kolekci poptavky_project/users/items
  const isCurrentUserAdmin = Boolean(
    currentAppUser && (currentAppUser.isAdmin === true || currentAppUser.role === 'admin')
  )

  const handleNavigate = (path: string) => {
    let targetPath = path
    if (path === 'landing') targetPath = '/'
    else if (path === 'new_inquiry') targetPath = '/zadat-poptavku'
    else if (path === 'client_dashboard') targetPath = '/moje-poptavky'
    else if (path === 'company_portal') targetPath = '/firma'
    else if (path === 'admin_portal') targetPath = '/admin'
    else if (path === 'privacy') targetPath = '/privacy'
    else if (path === 'terms') targetPath = '/terms'

    if (typeof window !== 'undefined' && window.location.pathname !== targetPath) {
      window.history.pushState({ path: targetPath }, '', targetPath)
    }

    const applyViewChange = () => {
      if (targetPath === '/') {
        setActiveView('landing')
      } else if (targetPath === '/privacy' || targetPath === '/ochrana-soukromi') {
        setActiveView('privacy')
      } else if (targetPath === '/terms' || targetPath === '/podminky') {
        setActiveView('terms')
      } else if (targetPath.startsWith('/zadat-poptavku')) {
        setActiveView('new_inquiry')
      } else if (targetPath.startsWith('/hodnoceni')) {
        const parts = targetPath.split('/')
        const inqId = parts[2]
        setReviewInquiryId(inqId || undefined)
        setActiveView('review_form')
      } else if (targetPath.startsWith('/firma')) {
        setActiveView('company_portal')
        if (targetPath === '/firma/nabidky') setCompanySubTab('my_offers')
        else if (targetPath === '/firma/kredity') setCompanySubTab('credits')
        else if (targetPath === '/firma/profil') setCompanySubTab('profile')
        else if (targetPath === '/firma/registrace') setCompanySubTab('register')
        else setCompanySubTab('feed')
      } else if (targetPath.startsWith('/moje-poptavky') || targetPath.startsWith('/klient')) {
        setActiveView('client_dashboard')
      } else if (targetPath.startsWith('/admin')) {
        setActiveView('admin_portal')
      } else {
        setActiveView('landing')
      }
    }

    if (typeof document !== 'undefined' && 'startViewTransition' in document) {
      ;(document as any).startViewTransition(() => {
        applyViewChange()
      })
    } else {
      applyViewChange()
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCompanySubTabChange = (
    tab: 'feed' | 'my_offers' | 'credits' | 'profile' | 'register'
  ) => {
    setCompanySubTab(tab)
    const pathMap: Record<string, string> = {
      feed: '/firma/zakazky',
      my_offers: '/firma/nabidky',
      credits: '/firma/kredity',
      profile: '/firma/profil',
      register: '/firma/registrace',
    }
    const path = pathMap[tab] || '/firma'
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState({ path }, '', path)
    }
  }

  // Dynamická aktualizace SEO meta tagů a titulku podle otevřené sekce a kategorie
  useEffect(() => {
    const mappedSeoView =
      activeView === 'new_inquiry'
        ? 'new_inquiry'
        : activeView === 'client_dashboard'
        ? 'my_inquiries'
        : activeView === 'admin_portal'
        ? 'admin'
        : activeView === 'review_form'
        ? 'review_form'
        : 'landing'

    updateDocumentSeo({
      view: mappedSeoView,
      category: selectedCategory,
      categories: selectedCategories,
    })
  }, [activeView, selectedCategory, selectedCategories])

  useEffect(() => {
    registerBrowserAiAgentTools({
      onOpenPrefilledWizard: (prefill) => {
        setUrlPrefill(prefill)
        if (prefill.categories && prefill.categories.length > 0) {
          setSelectedCategory(prefill.categories[0])
          setSelectedCategories(prefill.categories)
        }
        setActiveView('new_inquiry')
      },
    })
  }, [])

  useEffect(() => {
    const applyUrlRoute = () => {
      if (typeof window === 'undefined') return
      const pathname = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/'
      const parsedPrefill = parseUrlInquiryParams(window.location.search)

      if (parsedPrefill.hasAnyParam) {
        setUrlPrefill(parsedPrefill)
        if (parsedPrefill.categories && parsedPrefill.categories.length > 0) {
          setSelectedCategory(parsedPrefill.categories[0])
          setSelectedCategories(parsedPrefill.categories)
        }
      }

      if (pathname === '/privacy' || pathname === '/ochrana-soukromi') {
        setActiveView('privacy')
      } else if (pathname === '/terms' || pathname === '/podminky') {
        setActiveView('terms')
      } else if (pathname.startsWith('/admin')) {
        setActiveView('admin_portal')
      } else if (pathname.startsWith('/hodnoceni')) {
        const parts = pathname.split('/')
        const inqId = parts[2]
        setReviewInquiryId(inqId || undefined)
        setActiveView('review_form')
      } else if (pathname.startsWith('/firma')) {
        setActiveView('company_portal')
        if (pathname === '/firma/nabidky') setCompanySubTab('my_offers')
        else if (pathname === '/firma/kredity') setCompanySubTab('credits')
        else if (pathname === '/firma/profil') setCompanySubTab('profile')
        else if (pathname === '/firma/registrace') setCompanySubTab('register')
        else setCompanySubTab('feed')
      } else if (pathname.startsWith('/moje-poptavky') || pathname.startsWith('/klient')) {
        setActiveView('client_dashboard')
      } else if (pathname.startsWith('/zadat-poptavku') || parsedPrefill.hasAnyParam) {
        setActiveView('new_inquiry')
      } else {
        setActiveView('landing')
      }
    }

    applyUrlRoute()
    window.addEventListener('popstate', applyUrlRoute)

    // Sledování stavu Google přihlášení a synchronizace s poptavky_project/users/items/{uid}
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (u && !u.isAnonymous) {
        setGoogleUser(u)
        setCurrentUserUid(u.uid)
        const synced = await syncAndLoadAppUser(u)
        setCurrentAppUser(synced)
        setUsers((prev) => {
          const filtered = prev.filter((item) => item.uid !== synced.uid)
          return [synced, ...filtered]
        })
      } else {
        setGoogleUser(null)
        setCurrentAppUser(null)
        if (u?.uid) setCurrentUserUid(u.uid)
      }
    })

    ensureAuthSession().then((u) => {
      if (u?.uid) setCurrentUserUid(u.uid)
    })

    // Odložíme síťové načtení dat z Firestore až po prvním vykreslení stránky (LCP), aby Lighthouse měřil čistý a okamžitý render
    const loadTimer = window.setTimeout(() => {
      loadAllData().then((data) => {
        setCompanies(data.companies)
        setInquiries(data.inquiries)
        setOffers(data.offers)
        setReviews(data.reviews || [])
        setTransactions(data.transactions)
        if (data.users) setUsers(data.users)
        if (data.companies[0]) {
          setActiveCompanyId(data.companies[0].id)
        }
      })
    }, 900)

    return () => {
      window.clearTimeout(loadTimer)
      window.removeEventListener('popstate', applyUrlRoute)
      unsubAuth()
    }
  }, [])

  const handleGoogleSignIn = async () => {
    try {
      const user = await loginWithGoogle()
      const synced = await syncAndLoadAppUser(user)
      setGoogleUser(user)
      setCurrentAppUser(synced)
      setUsers((prev) => {
        const filtered = prev.filter((item) => item.uid !== synced.uid)
        return [synced, ...filtered]
      })
      showToast(
        synced.isAdmin
          ? `Vítejte, ${synced.displayName}! Váš účet má oprávnění Administrátora.`
          : `Jste přihlášeni jako ${synced.displayName}.`
      )
    } catch (err: any) {
      if (err?.code !== 'auth/popup-closed-by-user') {
        showToast('Přihlášení přes Google se nezdařilo. Zkuste to prosím znovu.')
      }
    }
  }

  const handleSignOut = async () => {
    await logoutAccount()
    setGoogleUser(null)
    setCurrentAppUser(null)
    if (activeView === 'admin_portal') {
      handleNavigate('/')
    }
    showToast('Byli jste úspěšně odhlášeni.')
  }

  // 1. Klient zadá novou poptávku (nebo více poptávek při rozdělení oborů)
  const handleCreateInquiry = async (newInq: Inquiry) => {
    await saveInquiryRecord(newInq)
    setInquiries((prev) => [newInq, ...prev])
    showToast(`Vaše poptávka ${newInq.publicCode} byla zveřejněna a rozeslána zhotovitelům v okolí!`)
  }

  const handleCreateMultipleInquiries = async (newInqs: Inquiry[]) => {
    for (const item of newInqs) {
      await saveInquiryRecord(item)
    }
    setInquiries((prev) => [...newInqs, ...prev])
    if (newInqs.length === 1) {
      showToast(`Vaše poptávka ${newInqs[0].publicCode} byla zveřejněna a rozeslána zhotovitelům v okolí!`)
    } else {
      showToast(
        `Bylo zveřejněno ${newInqs.length} samostatných poptávek podle vybraných oborů a rozesláno zhotovitelům v okolí!`
      )
    }
  }

  // 2. Klient vybere 1 vítěznou firmu -> Vítězi se strhnou zablokované kredity a odemknou kontakty,
  // ostatním firmám se zablokované kredity automaticky uvolní zpět!
  const handleAwardOffer = async (inquiry: Inquiry, winningOffer: InquiryOffer) => {
    const now = Date.now()

    const updatedInquiry: Inquiry = {
      ...inquiry,
      status: 'awarded',
      winningOfferId: winningOffer.id,
      awardedCompanyId: winningOffer.companyId,
      awardedCompanyName: winningOffer.companyName,
      awardedAt: now,
      updatedAt: now,
    }
    await saveInquiryRecord(updatedInquiry)
    setInquiries((prev) => prev.map((i) => (i.id === updatedInquiry.id ? updatedInquiry : i)))

    const relatedOffers = offers.filter((o) => o.inquiryId === inquiry.id)
    const updatedCompaniesMap = new Map<string, CompanyProfile>()
    companies.forEach((c) => updatedCompaniesMap.set(c.id, { ...c }))

    const newTransactions: CreditTransaction[] = []
    const updatedOffersList: InquiryOffer[] = []

    for (const off of relatedOffers) {
      const comp = updatedCompaniesMap.get(off.companyId)
      if (off.id === winningOffer.id) {
        const updatedWinOffer: InquiryOffer = {
          ...off,
          status: 'awarded',
          updatedAt: now,
        }
        await saveOfferRecord(updatedWinOffer)
        updatedOffersList.push(updatedWinOffer)

        if (comp) {
          comp.credits = Math.max(0, comp.credits - off.blockedCreditsAmount)
          comp.blockedCredits = Math.max(0, comp.blockedCredits - off.blockedCreditsAmount)
          comp.updatedAt = now
          await saveCompanyRecord(comp)

          const winTx: CreditTransaction = {
            id: `tx_win_${now}_${off.id}`,
            companyId: comp.id,
            type: 'offer_deduct',
            amount: -off.blockedCreditsAmount,
            inquiryId: inquiry.id,
            offerId: off.id,
            title: `Získaná zakázka ${inquiry.publicCode} – odemčení kontaktů klienta`,
            description: `Klient si vybral vaši nabídku! Rezervovaných ${off.blockedCreditsAmount} kreditů bylo vyúčtováno a kontakty jsou odemčeny.`,
            balanceAfter: comp.credits - comp.blockedCredits,
            createdAt: now,
          }
          await saveTransactionRecord(winTx)
          newTransactions.push(winTx)
        }
      } else if (off.status === 'pending') {
        const updatedRejOffer: InquiryOffer = {
          ...off,
          status: 'rejected',
          updatedAt: now,
        }
        await saveOfferRecord(updatedRejOffer)
        updatedOffersList.push(updatedRejOffer)

        if (comp) {
          comp.blockedCredits = Math.max(0, comp.blockedCredits - off.blockedCreditsAmount)
          comp.updatedAt = now
          await saveCompanyRecord(comp)

          const unblockTx: CreditTransaction = {
            id: `tx_unblock_${now}_${off.id}`,
            companyId: comp.id,
            type: 'offer_unblock',
            amount: off.blockedCreditsAmount,
            inquiryId: inquiry.id,
            offerId: off.id,
            title: `Uvolnění rezervovaných kreditů (${inquiry.publicCode})`,
            description: `Klient si u zakázky ${inquiry.publicCode} vybral jinou firmu. Rezervovaných ${off.blockedCreditsAmount} kreditů vám bylo vráceno zpět.`,
            balanceAfter: comp.credits - comp.blockedCredits,
            createdAt: now,
          }
          await saveTransactionRecord(unblockTx)
          newTransactions.push(unblockTx)
        }
      }
    }

    setOffers((prev) =>
      prev.map((o) => updatedOffersList.find((u) => u.id === o.id) || o)
    )
    setCompanies(Array.from(updatedCompaniesMap.values()))
    if (newTransactions.length > 0) {
      setTransactions((prev) => [...newTransactions, ...prev])
    }

    showToast(
      `Vybrali jste firmu ${winningOffer.companyName}! Kontakty byly oběma stranám zpřístupněny a ostatním firmám se uvolnily rezervované kredity.`
    )
  }

  // 3. Klient zruší poptávku -> Všem firmám se uvolní rezervované kredity
  const handleCancelInquiry = async (inquiry: Inquiry) => {
    const now = Date.now()
    const updatedInq: Inquiry = {
      ...inquiry,
      status: 'cancelled',
      updatedAt: now,
    }
    await saveInquiryRecord(updatedInq)
    setInquiries((prev) => prev.map((i) => (i.id === updatedInq.id ? updatedInq : i)))

    const pendingOffers = offers.filter(
      (o) => o.inquiryId === inquiry.id && o.status === 'pending'
    )
    const updatedCompaniesMap = new Map<string, CompanyProfile>()
    companies.forEach((c) => updatedCompaniesMap.set(c.id, { ...c }))

    for (const off of pendingOffers) {
      const rejOff: InquiryOffer = { ...off, status: 'rejected', updatedAt: now }
      await saveOfferRecord(rejOff)
      const comp = updatedCompaniesMap.get(off.companyId)
      if (comp) {
        comp.blockedCredits = Math.max(0, comp.blockedCredits - off.blockedCreditsAmount)
        await saveCompanyRecord(comp)
      }
    }

    setOffers((prev) =>
      prev.map((o) =>
        o.inquiryId === inquiry.id && o.status === 'pending'
          ? { ...o, status: 'rejected', updatedAt: now }
          : o
      )
    )
    setCompanies(Array.from(updatedCompaniesMap.values()))
    showToast(`Poptávka ${inquiry.publicCode} byla ukončena a rezervované kredity uvolněny.`)
  }

  // 4. Firma odešle nabídku -> Kredity se pouze ZABLOKUJÍ (rezervují)
  const handleSubmitOffer = async (
    company: CompanyProfile,
    inquiry: Inquiry,
    offerData: {
      priceEstimate: number
      priceNote: string
      earliestStartDate: string
      estimatedDuration: string
      message: string
    }
  ) => {
    const now = Date.now()
    const newOffer: InquiryOffer = {
      id: `off_${now.toString(36)}`,
      inquiryId: inquiry.id,
      inquiryTitle: inquiry.title,
      inquiryCode: inquiry.publicCode,
      companyId: company.id,
      companyName: company.name,
      companyIco: company.ico,
      companyCity: company.address.city,
      companyVerified: company.verified,
      companyRating: company.rating.score,
      companyPhone: company.phone,
      companyEmail: company.email,
      companyContactPerson: company.contactPerson,
      priceEstimate: offerData.priceEstimate,
      priceNote: offerData.priceNote,
      earliestStartDate: offerData.earliestStartDate,
      estimatedDuration: offerData.estimatedDuration,
      message: offerData.message,
      blockedCreditsAmount: inquiry.creditCost,
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }

    const updatedCompany: CompanyProfile = {
      ...company,
      blockedCredits: company.blockedCredits + inquiry.creditCost,
      updatedAt: now,
    }

    const updatedInquiry: Inquiry = {
      ...inquiry,
      offersCount: inquiry.offersCount + 1,
      updatedAt: now,
    }

    const blockTx: CreditTransaction = {
      id: `tx_block_${now}`,
      companyId: company.id,
      type: 'offer_block',
      amount: -inquiry.creditCost,
      inquiryId: inquiry.id,
      offerId: newOffer.id,
      title: `Rezervace kreditů za nabídku (${inquiry.publicCode})`,
      description: `Dočasně rezervováno ${inquiry.creditCost} kreditů. Strhnou se jen tehdy, pokud si vás klient vybere.`,
      balanceAfter: updatedCompany.credits - updatedCompany.blockedCredits,
      createdAt: now,
    }

    await Promise.all([
      saveOfferRecord(newOffer),
      saveCompanyRecord(updatedCompany),
      saveInquiryRecord(updatedInquiry),
      saveTransactionRecord(blockTx),
    ])

    setOffers((prev) => [newOffer, ...prev])
    setCompanies((prev) => prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c)))
    setInquiries((prev) => prev.map((i) => (i.id === updatedInquiry.id ? updatedInquiry : i)))
    setTransactions((prev) => [blockTx, ...prev])

    showToast(
      `Nabídka byla odeslána klientovi! Dočasně rezervováno ${inquiry.creditCost} kreditů.`
    )
  }

  // 5. Registrace nové firmy přes ARES
  const handleRegisterCompany = async (newCompany: CompanyProfile) => {
    const bonusTx: CreditTransaction = {
      id: `tx_bonus_${Date.now()}`,
      companyId: newCompany.id,
      type: 'starter_bonus',
      amount: 100,
      title: 'Startovní kredit zdarma pro novou firmu',
      description: 'Uvítací bonus 100 kreditů zdarma do začátku.',
      balanceAfter: 100,
      createdAt: Date.now(),
    }

    await Promise.all([saveCompanyRecord(newCompany), saveTransactionRecord(bonusTx)])
    setCompanies((prev) => [newCompany, ...prev])
    setTransactions((prev) => [bonusTx, ...prev])
    setActiveCompanyId(newCompany.id)
    showToast(
      `Firma ${newCompany.name} byla zaregistrována se 100 kredity zdarma a čeká na schválení.`
    )
  }

  // 6. Dobití kreditů
  const handleTopUpCredits = async (company: CompanyProfile, amount: number) => {
    const now = Date.now()
    const updated: CompanyProfile = {
      ...company,
      credits: company.credits + amount,
      updatedAt: now,
    }
    const tx: CreditTransaction = {
      id: `tx_topup_${now}`,
      companyId: company.id,
      type: 'credit_purchase',
      amount,
      title: `Dobití +${amount} kreditů`,
      description: 'Navýšení kreditního účtu firmy.',
      balanceAfter: updated.credits - updated.blockedCredits,
      createdAt: now,
    }
    await Promise.all([saveCompanyRecord(updated), saveTransactionRecord(tx)])
    setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
    setTransactions((prev) => [tx, ...prev])
    showToast(`Kreditní účet firmy ${company.name} byl navýšen o +${amount} kreditů.`)
  }

  // 7. Ověření firmy v Admin panelu
  const handleVerifyCompany = async (company: CompanyProfile, approved: boolean) => {
    const now = Date.now()
    const updated: CompanyProfile = {
      ...company,
      verified: approved,
      verificationStatus: approved ? 'verified' : 'rejected',
      verifiedAt: approved ? now : undefined,
      updatedAt: now,
    }
    await saveCompanyRecord(updated)
    setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
    showToast(
      approved
        ? `Firma ${company.name} (IČO ${company.ico}) byla úspěšně ověřena a schválena!`
        : `Ověření firmy ${company.name} bylo odebráno.`
    )
  }

  // 8. Nastavení / odebrání administrátora v kolekci poptavky_project/users/items
  const handleToggleUserAdmin = async (user: AppUser, makeAdmin: boolean) => {
    const updated: AppUser = {
      ...user,
      isAdmin: makeAdmin,
      role: makeAdmin ? 'admin' : user.companyId ? 'craftsman' : 'client',
      updatedAt: Date.now(),
    }
    await saveUserRecord(updated)
    setUsers((prev) => prev.map((u) => (u.uid === updated.uid ? updated : u)))
    if (currentAppUser && currentAppUser.uid === updated.uid) {
      setCurrentAppUser(updated)
    }
    showToast(
      makeAdmin
        ? `Uživatel ${user.displayName || user.email} byl nastaven jako Administrátor.`
        : `Uživateli ${user.displayName || user.email} bylo odebráno oprávnění Administrátora.`
    )
  }

  // 9. Smazání poptávky administrátorem
  const handleDeleteInquiry = async (inquiryId: string) => {
    await deleteInquiryRecord(inquiryId)
    setInquiries((prev) => prev.filter((i) => i.id !== inquiryId))
    showToast('Poptávka byla úspěšně odstraněna.')
  }

  // 10. Uložení hodnocení firmy klientem
  const handleSubmitReview = async (review: CompanyReview) => {
    await saveReviewRecord(review)
    const updatedReviews = [review, ...reviews.filter((r) => r.id !== review.id)]
    setReviews(updatedReviews)

    // Přepočítáme průměrné hodnocení rating.score a rating.count pro hodnocenou firmu
    const companyReviews = updatedReviews.filter((r) => r.companyId === review.companyId)
    const comp = companies.find((c) => c.id === review.companyId)
    if (comp) {
      const sum = companyReviews.reduce((acc, r) => acc + r.rating, 0)
      const avg =
        companyReviews.length > 0
          ? Number((sum / companyReviews.length).toFixed(1))
          : review.rating
      const updatedCompany: CompanyProfile = {
        ...comp,
        rating: {
          score: avg,
          count: companyReviews.length,
        },
        updatedAt: Date.now(),
      }
      await saveCompanyRecord(updatedCompany)
      setCompanies((prev) => prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c)))
    }

    // Pokud existuje příslušná poptávka, nastavíme jí reviewedAt, reviewId, reviewRating
    if (review.inquiryId) {
      const inq = inquiries.find((i) => i.id === review.inquiryId)
      if (inq) {
        const updatedInq: Inquiry = {
          ...inq,
          reviewedAt: Date.now(),
          reviewId: review.id,
          reviewRating: review.rating,
          updatedAt: Date.now(),
        }
        await saveInquiryRecord(updatedInq)
        setInquiries((prev) => prev.map((i) => (i.id === updatedInq.id ? updatedInq : i)))
      }
    }

    showToast(`Děkujeme! Vaše hodnocení pro firmu ${review.companyName} bylo úspěšně uloženo.`)
    handleNavigate('/moje-poptavky')
  }

  const pendingCompaniesCount = companies.filter((c) => c.verificationStatus === 'pending').length

  // Poptávky výhradně přihlášeného klienta
  const myInquiries = googleUser
    ? inquiries.filter((inq) => inq.clientUid === googleUser.uid)
    : []

  return (
    <div className="min-h-screen flex flex-col w-full max-w-full overflow-x-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200">
      {/* HORNÍ NAVIGAČNÍ LIŠTA */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-2.5 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-1.5 sm:gap-3">
          {/* Vlevo: Logo a čisté záložky Úvod a Zadat poptávku */}
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <button
              type="button"
              onClick={() => handleNavigate('/')}
              aria-label="NaceniTo.cz – Přejít na úvodní stránku"
              className="flex items-center gap-2 sm:gap-2.5 text-left cursor-pointer shrink-0"
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-brand-600 text-white flex items-center justify-center shadow-sm shrink-0">
                <Hammer className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="hidden sm:block">
                <div className="font-extrabold text-base sm:text-lg tracking-tight text-slate-900 dark:text-white leading-none">
                  Naceni<span className="text-brand-600 dark:text-brand-400">To.cz</span>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                  Chytré nacenění a poptávky řemesel
                </div>
              </div>
            </button>

            {/* Pouze čisté záložky Úvod a Zadat poptávku */}
            <nav className="flex items-center gap-1 sm:gap-2" aria-label="Hlavní navigace">
              <button
                type="button"
                onClick={() => handleNavigate('/')}
                className={`inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                  activeView === 'landing'
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden sm:inline">Úvod</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAiDraft(null)
                  setUrlPrefill(null)
                  setSelectedCategory(undefined)
                  setSelectedCategories([])
                  handleNavigate('/zadat-poptavku')
                }}
                className={`inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                  activeView === 'new_inquiry'
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <PlusCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden sm:inline">Zadat poptávku</span>
                <span className="sm:hidden">Poptat</span>
                {hasSavedDraft && activeView !== 'new_inquiry' && (
                  <>
                    <span
                      className="hidden sm:inline ml-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black leading-none"
                      title="Máte uloženou rozpracovanou poptávku"
                    >
                      Rozpracováno
                    </span>
                    <span
                      className="sm:hidden ml-0.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0 animate-pulse"
                      title="Máte uloženou rozpracovanou poptávku"
                    />
                  </>
                )}
              </button>
            </nav>
          </div>

          {/* Vpravo: ThemeSwitcher + (UserProfileMenu pokud přihlášen, jinak Přihlásit přes Google) */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <ThemeSwitcher />

            {googleUser ? (
              <UserProfileMenu
                googleUser={googleUser}
                appUser={currentAppUser}
                myInquiriesCount={myInquiries.length}
                isCurrentUserAdmin={isCurrentUserAdmin}
                pendingAdminCount={pendingCompaniesCount}
                activeView={activeView}
                onNavigate={handleNavigate}
                onSignOut={handleSignOut}
              />
            ) : (
              <button
                type="button"
                onMouseEnter={prewarmGoogleAuthPopup}
                onFocus={prewarmGoogleAuthPopup}
                onClick={handleGoogleSignIn}
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-white font-bold text-xs sm:text-sm shadow-2xs transition-all cursor-pointer whitespace-nowrap"
              >
                <GoogleIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden sm:inline">Přihlásit přes Google</span>
                <span className="sm:hidden text-xs">Přihlásit</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* OZNÁMENÍ (TOAST) */}
      {toastMessage && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 bg-slate-900 dark:bg-emerald-600 text-white px-4 py-3.5 rounded-2xl shadow-xl border border-slate-700 flex items-center gap-3 animate-scale-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 dark:text-white shrink-0" />
          <span className="text-xs sm:text-sm font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* HLAVNÍ OBSAH STRÁNKY */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {activeView === 'landing' && (
          <LandingPage
            inquiries={inquiries}
            companies={companies}
            onSelectCategoryAndNewInquiry={(cat) => {
              setAiDraft(null)
              setUrlPrefill(null)
              setSelectedCategory(cat)
              setSelectedCategories([cat])
              handleNavigate('/zadat-poptavku')
            }}
            onSelectCategoriesAndNewInquiry={(cats) => {
              setAiDraft(null)
              setUrlPrefill(null)
              setSelectedCategory(cats[0])
              setSelectedCategories(cats)
              handleNavigate('/zadat-poptavku')
            }}
            onResumeSavedDraft={() => {
              setAiDraft(null)
              setUrlPrefill(null)
              setSelectedCategory(undefined)
              setSelectedCategories([])
              handleNavigate('/zadat-poptavku')
            }}
            onGenerateAiDraft={async (promptText) => {
              const draft = await generateAiInquiryTemplate(promptText)
              setUrlPrefill(null)
              setAiDraft(draft)
              setSelectedCategory(draft.categories[0])
              setSelectedCategories(draft.categories)
              handleNavigate('/zadat-poptavku')
            }}
            onNavigate={handleNavigate}
            onInspectCompany={(comp) => setInspectedCompany(comp)}
          />
        )}

        <Suspense fallback={<PageLoadingSkeleton />}>
          {activeView === 'new_inquiry' && (
            <NewInquiryWizard
              currentUserUid={currentUserUid}
              googleUser={googleUser}
              onGoogleSignIn={handleGoogleSignIn}
              onSubmitInquiry={handleCreateInquiry}
              onSubmitMultipleInquiries={handleCreateMultipleInquiries}
              onSwitchToClientDashboard={() => handleNavigate('/moje-poptavky')}
              initialCategory={selectedCategory}
              initialCategories={selectedCategories}
              initialAiDraft={aiDraft}
              urlPrefill={urlPrefill}
              onGenerateAiDraft={async (promptText) => {
                const draft = await generateAiInquiryTemplate(promptText)
                setAiDraft(draft)
                return draft
              }}
            />
          )}

          {activeView === 'review_form' && (
            <ReviewFormView
              inquiry={inquiries.find((i) => i.id === reviewInquiryId)}
              company={companies.find((c) => {
                const matchedInq = inquiries.find((i) => i.id === reviewInquiryId)
                return c.id === matchedInq?.awardedCompanyId
              })}
              companies={companies}
              inquiries={inquiries}
              currentUserUid={currentUserUid}
              defaultClientName={currentAppUser?.displayName || googleUser?.displayName || ''}
              onSubmitReview={handleSubmitReview}
              onBack={() => handleNavigate('/moje-poptavky')}
            />
          )}

          {activeView === 'client_dashboard' &&
            (googleUser ? (
              <ClientDashboard
                inquiries={myInquiries}
                offers={offers}
                companies={companies}
                reviews={reviews}
                onAwardOffer={handleAwardOffer}
                onCancelInquiry={handleCancelInquiry}
                onCreateNewClick={() => {
                  setSelectedCategory(undefined)
                  setSelectedCategories([])
                  handleNavigate('/zadat-poptavku')
                }}
                onInspectCompany={(comp) => setInspectedCompany(comp)}
                onOpenReviewForm={(inq) => {
                  setReviewInquiryId(inq.id)
                  handleNavigate('/hodnoceni/' + inq.id)
                }}
              />
            ) : (
              <div className="max-w-lg mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 text-center space-y-4 shadow-sm animate-scale-in">
                <div className="w-12 h-12 rounded-2xl bg-brand-100 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 flex items-center justify-center mx-auto">
                  <ClipboardList className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                  Moje poptávky
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Pro zobrazení vašich zadaných poptávek a došlých nabídek od řemeslníků se prosím přihlaste přes svůj Google účet.
                </p>
                <button
                  type="button"
                  onMouseEnter={prewarmGoogleAuthPopup}
                  onFocus={prewarmGoogleAuthPopup}
                  onClick={handleGoogleSignIn}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm shadow-sm cursor-pointer"
                >
                  <GoogleIcon className="w-4 h-4 bg-white rounded-full p-0.5" />
                  <span>Přihlásit se přes Google</span>
                </button>
              </div>
            ))}

          {activeView === 'company_portal' && (
            <CompanyPortal
              companies={companies}
              activeCompanyId={activeCompanyId}
              onSelectActiveCompany={setActiveCompanyId}
              onRegisterCompany={handleRegisterCompany}
              onUpdateCompany={async (updated) => {
                await saveCompanyRecord(updated)
                setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
                showToast('Nastavení firmy bylo uloženo.')
              }}
              onTopUpCredits={handleTopUpCredits}
              inquiries={inquiries}
              offers={offers}
              transactions={transactions}
              onSubmitOffer={handleSubmitOffer}
              activeSubTab={companySubTab}
              onSubTabChange={handleCompanySubTabChange}
            />
          )}

          {activeView === 'admin_portal' &&
            (isCurrentUserAdmin ? (
              <AdminPortal
                companies={companies}
                inquiries={inquiries}
                offers={offers}
                users={users}
                onVerifyCompany={handleVerifyCompany}
                onAdminAddCredits={handleTopUpCredits}
                onDeleteInquiry={handleDeleteInquiry}
                onToggleUserAdmin={handleToggleUserAdmin}
              />
            ) : (
              <div className="max-w-lg mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 text-center space-y-4 shadow-sm animate-scale-in">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                  Přístup pouze pro administrátora
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Pro vstup do administrace a schvalování firem se prosím přihlaste přes svůj Google
                  účet, který má v tomto projektu nastaveno oprávnění administrátora.
                </p>
                {!googleUser && (
                  <button
                    type="button"
                    onMouseEnter={prewarmGoogleAuthPopup}
                    onFocus={prewarmGoogleAuthPopup}
                    onClick={handleGoogleSignIn}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm shadow-sm cursor-pointer"
                  >
                    <GoogleIcon className="w-4 h-4 bg-white rounded-full p-0.5" />
                    <span>Přihlásit se přes Google</span>
                  </button>
                )}
              </div>
            ))}

          {(activeView === 'privacy' || activeView === 'terms') && (
            <LegalPagesView
              mode={activeView}
              onBack={() => handleNavigate('/')}
              onSwitchMode={(nextMode) => handleNavigate('/' + nextMode)}
            />
          )}
        </Suspense>
      </main>

      {/* Globální modální okno pro hodnocení a recenze firmy */}
      {inspectedCompany && (
        <Suspense fallback={null}>
          <CompanyReviewsModal
            company={inspectedCompany}
            reviews={reviews}
            onClose={() => setInspectedCompany(null)}
            onWriteReviewClick={(comp) => {
              setInspectedCompany(null)
              const matchedInq = myInquiries.find((i) => i.awardedCompanyId === comp.id)
              if (matchedInq) {
                setReviewInquiryId(matchedInq.id)
                handleNavigate('/hodnoceni/' + matchedInq.id)
              } else {
                handleNavigate('/hodnoceni')
              }
            }}
          />
        </Suspense>
      )}

      {/* PATIČKA */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-6 px-4 text-center text-xs text-slate-500 dark:text-slate-400">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-200">
            <Hammer className="w-4 h-4 text-brand-500" />
            <span>
              © {new Date().getFullYear()} Naceni<span className="text-brand-600 dark:text-brand-400">To.cz</span> • Všechna řemesla a práce na jednom místě
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => handleNavigate('/privacy')}
              className="hover:text-brand-600 dark:hover:text-brand-400 hover:underline cursor-pointer"
            >
              Ochrana soukromí
            </button>
            <span>•</span>
            <button
              type="button"
              onClick={() => handleNavigate('/terms')}
              className="hover:text-brand-600 dark:hover:text-brand-400 hover:underline cursor-pointer"
            >
              Smluvní podmínky
            </button>
            <span className="hidden sm:inline">•</span>
            <span>
              Provozovatel:{' '}
              <a
                href="https://hkcloud.cz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-brand-600 dark:text-brand-400 hover:underline"
              >
                hkcloud.cz
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  )
}
